import React, { useEffect, useState } from 'react';
import { IoCloudUpload, IoTrash, IoAdd, IoClose } from 'react-icons/io5';
import { getProfile, patchProfile, deleteBanner } from '../api/ProfileAPI';
import { uploadImage, MAX_IMAGE_BYTES, ACCEPTED_IMAGE_TYPES } from '../api/MediaAPI';
import ImageCropModal, { mediaErrorText } from '../components/ImageCropModal';
import { ProfileHeader } from './ProfileParts';
import { ProfileAbout, ProfileLinks, themedCardProps, LINK_PLATFORMS, linkIcon } from './ProfileContent';
import { useLanguage } from '../i18n/LanguageContext';
import { useSocket } from '../socket/SocketContext';
import '../style/Profile.css';

const ABOUT_MAX = 500;
const LINKS_MAX = 5;
const DEFAULT_COLORS = { primary: '#5b6cff', accent: '#ff6fb5' };

// Settings > profile appearance: banner, profile colours, "Hakkımda" and
// links, with the profile card previewed live as it would look to others.
// Its own Save (no password needed - nothing here is account security),
// separate from the credentials form below it. The banner, like the avatar,
// saves the moment it's cropped.
export default function ProfileCustomizeSection({ fullName, username, imageUrl, description }) {
  const { t } = useLanguage();
  const { pushToast } = useSocket();
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const me = localStorage.getItem('username');
    getProfile(me).then((res) => {
      if (res.status !== 'success') return;
      const p = res.profile;
      const initial = {
        banner: p.banner, bannerColor: p.bannerColor, profileColors: p.profileColors,
        aboutMe: p.aboutMe || '', links: p.links || []
      };
      setSaved(initial);
      setDraft(initial);
    });
  }, []);

  if (!draft) return null;

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const preview = { username, fullName, imageUrl, description, ...draft };
  const themed = themedCardProps(preview);
  const errorText = (res) => {
    const key = res.code ? `profileEdit.errors.${res.code}` : null;
    const text = key ? t(key) : null;
    return text && text !== key ? text : (res.code ? mediaErrorText(t, res) : t('profile.actionFailed'));
  };

  const save = async () => {
    setBusy(true);
    const { banner, ...fields } = draft;
    const res = await patchProfile({
      ...fields,
      links: fields.links.filter((l) => l.url.trim())
    }).catch(() => ({ status: 'fail' }));
    setBusy(false);
    if (res.status === 'success') {
      const next = { ...draft, links: res.profile.links, aboutMe: res.profile.aboutMe };
      setSaved(next);
      setDraft(next);
      pushToast(t('profileEdit.saved'));
    } else {
      pushToast(errorText(res));
    }
  };

  const pickBanner = (file) => {
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      pushToast(mediaErrorText(t, { code: 'FILE_TOO_LARGE' }));
      return;
    }
    setBannerFile(file);
  };

  const uploadBanner = async (crop) => {
    const file = bannerFile;
    setBannerFile(null);
    setBusy(true);
    const res = await uploadImage('api/users/banner', { file, crop });
    setBusy(false);
    if (res.status === 'success') {
      setSaved((s) => ({ ...s, banner: res.banner }));
      set({ banner: res.banner });
    } else {
      pushToast(mediaErrorText(t, res));
    }
  };

  const removeBanner = async () => {
    const res = await deleteBanner().catch(() => ({ status: 'fail' }));
    if (res.status === 'success') {
      setSaved((s) => ({ ...s, banner: null }));
      set({ banner: null });
    } else {
      pushToast(t('profile.actionFailed'));
    }
  };

  const setLink = (i, patch) => set({ links: draft.links.map((l, j) => (j === i ? { ...l, ...patch } : l)) });

  return (
    <section className="SettingsSection glassCard ProfileCustomize">
      <header className="SettingsSectionHead">
        <h5>{t('profileEdit.title')}</h5>
        <aside className="descGray">{t('profileEdit.hint')}</aside>
      </header>

      <div className="ProfileCustomizeGrid">
        <div className="ProfileCustomizeFields">
          <div className="ProfileField">
            <span className="ProfileFieldLabel">{t('profileEdit.banner')}</span>
            <div className="ProfileFieldRow">
              <label className="SendButton secondaryColor ProfileFieldButton">
                <IoCloudUpload /> {t('profileEdit.uploadBanner')}
                <input type="file" accept={ACCEPTED_IMAGE_TYPES} hidden onChange={(e) => { pickBanner(e.target.files[0]); e.target.value = ''; }} />
              </label>
              {draft.banner && (
                <button type="button" className="SendButton cancelColor ProfileFieldButton" onClick={removeBanner}><IoTrash /> {t('profileEdit.removeBanner')}</button>
              )}
            </div>
            {!draft.banner && (
              <div className="ProfileFieldRow">
                <input type="color" className="ProfileColorInput" value={draft.bannerColor || '#5865f2'} aria-label={t('profileEdit.bannerColor')}
                  onChange={(e) => set({ bannerColor: e.target.value })} />
                <span className="descGray">{t('profileEdit.bannerColor')}</span>
                {draft.bannerColor && <button type="button" className="ProfileLinkRemove" onClick={() => set({ bannerColor: null })} aria-label={t('profileEdit.reset')}><IoClose /></button>}
              </div>
            )}
          </div>

          <div className="ProfileField">
            <label className="ProfileFieldLabel ProfileToggle">
              <input type="checkbox" checked={!!draft.profileColors}
                onChange={(e) => set({ profileColors: e.target.checked ? DEFAULT_COLORS : null })} />
              {t('profileEdit.colors')}
            </label>
            {draft.profileColors && (
              <div className="ProfileFieldRow">
                <input type="color" className="ProfileColorInput" value={draft.profileColors.primary} aria-label={t('profileEdit.colorPrimary')}
                  onChange={(e) => set({ profileColors: { ...draft.profileColors, primary: e.target.value } })} />
                <input type="color" className="ProfileColorInput" value={draft.profileColors.accent} aria-label={t('profileEdit.colorAccent')}
                  onChange={(e) => set({ profileColors: { ...draft.profileColors, accent: e.target.value } })} />
                <span className="descGray">{t('profileEdit.colorsHint')}</span>
              </div>
            )}
          </div>

          <div className="ProfileField">
            <span className="ProfileFieldLabel">{t('profileEdit.about')}</span>
            <textarea className="ProfileAboutInput" rows={5} maxLength={ABOUT_MAX} value={draft.aboutMe}
              placeholder={t('profileEdit.aboutPlaceholder')} onChange={(e) => set({ aboutMe: e.target.value })} />
            <span className="descGray ProfileCounter">{[...draft.aboutMe].length}/{ABOUT_MAX} · {t('profileEdit.aboutFormatting')}</span>
          </div>

          <div className="ProfileField">
            <span className="ProfileFieldLabel">{t('profileEdit.links')}</span>
            {draft.links.map((l, i) => {
              const Icon = linkIcon(l.platform);
              return (
                <div className="ProfileFieldRow ProfileLinkRow" key={i}>
                  <Icon className="ProfileLinkIcon" />
                  <select className="SettingsSelect" value={l.platform} onChange={(e) => setLink(i, { platform: e.target.value })} aria-label={t('profileEdit.platform')}>
                    {LINK_PLATFORMS.map((pl) => <option key={pl} value={pl}>{t(`profileEdit.platforms.${pl}`)}</option>)}
                  </select>
                  <input type="url" className="ProfileLinkInput" value={l.url} placeholder="https://" inputMode="url"
                    autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    onChange={(e) => setLink(i, { url: e.target.value })} aria-label={t('profileEdit.url')} />
                  <button type="button" className="ProfileLinkRemove" onClick={() => set({ links: draft.links.filter((_, j) => j !== i) })} aria-label={t('profileEdit.removeLink')}><IoClose /></button>
                </div>
              );
            })}
            {draft.links.length < LINKS_MAX && (
              <button type="button" className="SendButton secondaryColor ProfileFieldButton ProfileAddLink"
                onClick={() => set({ links: [...draft.links, { platform: 'website', url: '' }] })}>
                <IoAdd /> {t('profileEdit.addLink')}
              </button>
            )}
          </div>

          <div className="ProfileFieldRow">
            <button type="button" className="SendButton primaryColor ProfileFieldButton" disabled={!dirty || busy} onClick={save}>{t('profileEdit.save')}</button>
            {dirty && <button type="button" className="SendButton secondaryColor ProfileFieldButton" onClick={() => setDraft(saved)}>{t('settings.discardChanges')}</button>}
          </div>
        </div>

        <div className="ProfileCustomizePreview">
          <span className="ProfileFieldLabel">{t('profileEdit.preview')}</span>
          <div className={`ProfileCard ProfilePreviewCard${themed.className}`} style={themed.style}>
            <ProfileHeader p={preview} />
            <div className="ProfileCardBody">
              {preview.description && <p className="ProfileBio">{preview.description}</p>}
              <ProfileAbout text={preview.aboutMe} />
              <ProfileLinks links={preview.links.filter((l) => /^https:\/\/\S+\.\S+/.test(l.url))} />
            </div>
          </div>
        </div>
      </div>

      <ImageCropModal
        file={bannerFile}
        aspect={5 / 2}
        title={t('profileEdit.cropBanner')}
        onCancel={() => setBannerFile(null)}
        onConfirm={uploadBanner}
        onInvalid={() => { setBannerFile(null); pushToast(mediaErrorText(t, { code: 'UNSUPPORTED_TYPE' })); }}
      />
    </section>
  );
}
